import styles from "./toolbox-point-style.module.css";
import { PointIconName } from "PosterEditor/model/poster-types";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { PointStyle } from "PosterEditor/model/poster-types";
import { Toolbox } from "PosterEditor/sidebar/toolboxes/toolbox";
import { ToolboxComponentSection as Section } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-section";
import { ToolboxComponentPointIconGrid } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-point-icon-grid";
import { ToolboxSectionDivider as Divider } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-section";
import { ToolboxComponentColorPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-color-picker";
import { ToolboxComponentSlider as Slider } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-slider";
import { ToolboxComponentCheckbox } from "../../toolbox-components/toolbox-component-checkbox";
import { useEffect, useState } from "react";
import { pickerColors, StyledPoint } from "PosterEditor/model/poster-project";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import { DeleteButton } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import { PointEditingPermissions } from "PosterEditor/model/editing-permissions";
import { ToolboxComponentToggle as Toggle } from "../../toolbox-components/toolbox-component-toggle";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { v4 as uuid } from "uuid";
import { BackButton } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import {
  useImageStore,
  SaveImageAtURLConfig,
} from "entity-store/image-store/use-image-store";
import { filterClasses } from "PosterEditor/utils/filter-classes";
import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";
import { ToolboxMapLabelStyle } from "../labels/toolbox-label-style";
import { MapLabel } from "PosterEditor/model/poster-project";
import { UIIconButton } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import { ToolboxComponentVisibilityToggle } from "../../toolbox-components/toolbox-component-visibility-toggle";

export type Tab = "point" | "label";

interface ToolboxPointStyleProps {
  point?: StyledPoint;
  label?: MapLabel;
  selectedTab?: Tab;
}

export const ToolboxPointStyle = (props: ToolboxPointStyleProps) => {
  const [selectedTab, setSelectedTab] = useState<Tab>(
    props.selectedTab ?? "point"
  );
  const {
    project,
    isTemplateEditing,
    styledPointAtCoordinate,
    styledPointWithID,
  } = usePosterProject();
  const { deleteCoordinate } = usePosterProject();
  const { deleteImage } = useImageStore();
  const [selectedPoint, setSelectedPoint] = useState<StyledPoint | undefined>(
    props.point ?? styledPointWithID(props.label?.pointID ?? "")
  );

  const { setEditingElement, editorUIState } = useEditorUI();

  // We need this (for now at least) to ensure the point is always referenced from the project.
  // Otherwise after adding a point, editing the text, and changing tabs to the point style,
  // the text is reverted.
  const onEditPoint = () => {
    const coordinate = selectedPoint?.coordinate;
    if (!coordinate) return;
    const existingPoint = styledPointAtCoordinate(coordinate);
    if (existingPoint) {
      setSelectedPoint(existingPoint.point);
    }
  };

  useEffect(() => {
    const point = editorUIState.editingElement?.styledPoint;
    if (point) {
      setSelectedPoint(point);
    }
    if (editorUIState.editingElement?.mapLabel !== undefined) {
      setSelectedTab("label");
    } else {
      setSelectedTab("point");
    }
  }, [editorUIState.editingElement, props.label, props.point]);

  const allowDeletingPoint =
    isTemplateEditing ||
    (selectedPoint?.editingPermissions?.delete ??
      project.editingPermissions.point.delete);

  const deleteSelectedPoint = () => {
    if (!selectedPoint) return;
    deleteCoordinate(selectedPoint.coordinate);
    setEditingElement({ type: "mapLabel" });
    if (selectedPoint.style.imageName) {
      deleteImage(
        selectedPoint.style.imageName,
        "png",
        "pointIcons",
        isTemplateEditing
      );
    }
  };

  return (
    <div className={styles.container}>
      <Header
        allowDelete={allowDeletingPoint}
        onClickDelete={deleteSelectedPoint}
        point={selectedPoint}
      />
      <NavBar selectedTab={selectedTab} onSelectTab={setSelectedTab} />
      {selectedTab === "point" && selectedPoint && (
        <PointStyleToolbox point={selectedPoint} onChangePoint={onEditPoint} />
      )}
      {selectedTab === "label" && selectedPoint && (
        <ToolboxMapLabelStyle
          point={selectedPoint}
          onChangePoint={onEditPoint}
        />
      )}
    </div>
  );
};

interface PointStyleToolboxProps {
  point: StyledPoint;
  onChangePoint: () => void;
}

const PointStyleToolbox = (props: PointStyleToolboxProps) => {
  const { project, isTemplateEditing, cacheImageForPointStyle } =
    usePosterProject();
  const { saveImageAtURL, deletePointIconImage } = useImageStore();
  const { deleteCoordinate, setPointStyle, removeAllStyledPoints } =
    usePosterProject();
  const selectedCoordinate = props.point.coordinate;
  const [iconPickerOption, setIconPickerOption] = useState<number>(0);

  const [editingStyle, setEditingStyle] = useState<PointStyle>(
    props.point.style
  );
  const selectedPoint = props.point;

  useEffect(() => {
    setPointStyle(
      selectedCoordinate,
      editingStyle,
      props.point.id,
      props.point.label
    );
    props.onChangePoint();
  }, [editingStyle]);

  useEffect(() => {
    setEditingStyle(props.point.style);
    setIconPickerOption(props.point.style.imageName ? 1 : 0);
  }, [props.point]);

  const setIconName = (icon: PointIconName) => {
    if (editingStyle.imageName) {
      deletePointIconImage(project.id, editingStyle.imageName);
    }

    setEditingStyle((prev) => {
      return {
        ...prev,
        icon: icon,
        imageName: undefined,
      };
    });
  };

  const setMainColor = (color: string) => {
    setEditingStyle((prev) => {
      return {
        ...prev,
        mainColor: color,
      };
    });
  };

  const setSecondaryColor = (color: string) => {
    setEditingStyle((prev) => {
      return {
        ...prev,
        secondaryColor: color,
      };
    });
  };

  const setSize = (size: number) => {
    setEditingStyle((prev) => {
      return {
        ...prev,
        size: size,
      };
    });
  };

  const onSelectEmoji = (emoji: EmojiClickData) => {
    const url = emoji.imageUrl;

    const newName = uuid();

    const userFolder = `projects/${project.id}/pointIcons`;
    const saveConfig: SaveImageAtURLConfig = {
      url: url,
      name: newName,
      existingName: editingStyle.imageName,
      isPublic: isTemplateEditing,
      folder: isTemplateEditing ? "pointIcons" : userFolder,
    };

    saveImageAtURL(saveConfig)
      .then((newID) => {
        // loadSignedURLS();
        const style = { ...editingStyle, imageName: newName };
        cacheImageForPointStyle(style);
        setEditingStyle((prev) => {
          return {
            ...prev,
            icon: undefined,
            imageName: newName,
          };
        });
      })
      .catch((error) => {
        console.error("Error saving image at URL:", error);
      });
  };

  const removeStyleFromSelectedPoint = () => {
    if (selectedPoint?.coordinate) {
      setPointStyle(selectedPoint?.coordinate, undefined, undefined, undefined);
    }
  };

  const onSelectLandmarkText = (landmarkText: string) => {};
  const allowEditingIcon =
    isTemplateEditing ||
    (selectedPoint.editingPermissions?.icon ??
      project.editingPermissions.point.icon);
  const allowEditingSize =
    isTemplateEditing ||
    (selectedPoint.editingPermissions?.size ??
      project.editingPermissions.point.size);
  const allowEditingLineColor =
    isTemplateEditing ||
    (selectedPoint.editingPermissions?.lineColor ??
      project.editingPermissions.point.lineColor);
  const allowEditingFillColor =
    isTemplateEditing ||
    (selectedPoint.editingPermissions?.fillColor ??
      project.editingPermissions.point.fillColor);

  return (
    <Toolbox collapsible={false} showTitleBar={false}>
      <div className={styles.paddedBodyContent}>
        <ToolboxComponentVisibilityToggle
          text="POINT"
          isVisible={selectedPoint.style.showPoint ?? false}
          onToggle={() => {
            const newValue = !selectedPoint.style.showPoint;
            setEditingStyle((prev) => {
              return {
                ...prev,
                showPoint: newValue,
              };
            });
          }}
        />

        {selectedPoint.style.showPoint && (
          <>
            {allowEditingIcon && (
              <Section>
                <Divider whitespaceOnly />
                <Toggle
                  optionTitles={["Icon", "Emoji"]}
                  selectedOptionIndex={iconPickerOption}
                  onClickOption={setIconPickerOption}
                />
                {iconPickerOption === 0 ? (
                  <ToolboxComponentPointIconGrid
                    selectedIcon={editingStyle.icon}
                    onSelectIcon={setIconName}
                  />
                ) : (
                  <div className={styles.emojiPickerContainer}>
                    <EmojiPicker
                      open={true}
                      previewConfig={{ showPreview: false }}
                      onEmojiClick={(emoji) => {
                        onSelectEmoji(emoji);
                      }}
                    />
                  </div>
                )}
                <Divider whitespaceOnly />
              </Section>
            )}
            {iconPickerOption == 0 &&
              (allowEditingFillColor || allowEditingLineColor) && (
                <Section>
                  <ColorControls
                    mainColor={editingStyle.mainColor}
                    secondaryColor={editingStyle.secondaryColor ?? "white"}
                    onMainColorChange={setMainColor}
                    onSecondaryColorChange={setSecondaryColor}
                  />
                  <Divider whitespaceOnly />
                </Section>
              )}
            {allowEditingSize && (
              <Section>
                <Slider
                  title="Size"
                  min={10}
                  max={50}
                  value={editingStyle.size}
                  onChange={setSize}
                />
              </Section>
            )}
            {isTemplateEditing && (
              <>
                <Divider />
                <Admin point={selectedPoint} />
              </>
            )}
          </>
        )}
      </div>
    </Toolbox>
  );
};

interface HeaderProps {
  point?: StyledPoint;
  label?: MapLabel;
  allowDelete: boolean;
  onClickDelete: () => void;
}

const Header = (props: HeaderProps) => {
  const { setEditingElement } = useEditorUI();
  const selectedCoordinate = props.point?.coordinate ?? props.label?.coordinate;

  const textColor = getComputedStyle(document.documentElement).getPropertyValue(
    "--color-text-secondary"
  );

  const onClickBackButton = () => {
    setEditingElement({ type: "mapLabel" });
  };
  const latLongString = `${selectedCoordinate?.lat.toFixed(
    6
  )}, ${selectedCoordinate?.lng.toFixed(6)}`;
  return (
    <div className={styles.header}>
      <div className={styles.headerLeading}>
        <BackButton onClick={onClickBackButton} />

        {/* {props.point.style.icon && (
          <PointIcon
            iconName={props.point.style.icon}
            color={textColor}
            size={20}
          />
        )} */}
        <h1> {latLongString}</h1>
      </div>
      {props.allowDelete && (
        <div className={styles.headerTrailing}>
          <DeleteButton onClick={props.onClickDelete} />
        </div>
      )}
    </div>
  );
};

interface NavBarProps {
  selectedTab: Tab;
  onSelectTab: (tab: Tab) => void;
}
const NavBar = (props: NavBarProps) => {
  return (
    <div className={styles.tabBar}>
      <button
        className={filterClasses(
          styles.tabBarItem,
          props.selectedTab === "point" ? styles.tabBarItemSelected : null
        )}
        onClick={() => props.onSelectTab("point")}
      >
        <UIIcon iconName="POINT" size={25} variant="REGULAR" />
        <h2>Point</h2>
      </button>
      <button
        className={filterClasses(
          styles.tabBarItem,
          props.selectedTab === "label" ? styles.tabBarItemSelected : null
        )}
        onClick={() => props.onSelectTab("label")}
      >
        <UIIcon iconName="MAP-LABEL" size={20} variant="REGULAR" />
        <h2>Label</h2>
      </button>
    </div>
  );
};

interface ColorControlsProps {
  mainColor: string;
  secondaryColor: string;
  onMainColorChange: (color: string) => void;
  onSecondaryColorChange: (color: string) => void;
}

const ColorControls = (props: ColorControlsProps) => {
  const [isMainColorExpanded, setIsMainColorExpanded] =
    useState<boolean>(false);
  const [isSecondaryColorExpanded, setIsSecondaryColorExpanded] =
    useState<boolean>(false);

  return (
    <div className={styles.colorContainer}>
      <ToolboxComponentColorPicker
        title="Line color"
        colors={pickerColors}
        selectedColor={props.mainColor}
        onSelectColor={props.onMainColorChange}
        showSwatch={true}
        isExpanded={isMainColorExpanded}
        onToggleExpand={() => {
          setIsMainColorExpanded(!isMainColorExpanded);
        }}
      />
      <ToolboxComponentColorPicker
        title="Fill color"
        colors={pickerColors}
        selectedColor={props.secondaryColor}
        onSelectColor={props.onSecondaryColorChange}
        showSwatch={true}
        isExpanded={isSecondaryColorExpanded}
        onToggleExpand={() => {
          setIsSecondaryColorExpanded(!isSecondaryColorExpanded);
        }}
      />
    </div>
  );
};

interface AdminProps {
  point: StyledPoint;
}

const Admin = (props: AdminProps) => {
  const { project, setStyledPointEditingPermissions } = usePosterProject();

  const [permissions, setPermissions] = useState<PointEditingPermissions>(
    props.point.editingPermissions ?? project.editingPermissions.point
  );

  useEffect(() => {
    setPermissions(
      props.point.editingPermissions ?? project.editingPermissions.point
    );
  }, [props.point]);

  const updatePermissions = (
    key: keyof PointEditingPermissions,
    value: boolean
  ) => {
    const newPermissions = {
      ...permissions,
      [key]: value,
    };
    setPermissions(newPermissions);
    setStyledPointEditingPermissions(props.point, newPermissions);
  };

  return (
    <Section title="Editing permissions">
      {(Object.keys(permissions) as (keyof PointEditingPermissions)[]).map(
        (key) => (
          <ToolboxComponentCheckbox
            key={key}
            text={key.charAt(0).toUpperCase() + key.slice(1)}
            isSelected={permissions[key] === true}
            onClick={() => {
              const newValue = !permissions[key];
              updatePermissions(key, newValue);
            }}
          />
        )
      )}
    </Section>
  );
};
